import type React from 'react';
import { useId } from 'react';

import {
  Box,
  FieldSet,
  FormField,
  FormHelp,
  InlineFeedback,
  Input,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  Textarea,
} from '@zillow/constellation-10';

import type { Section } from '../../../../models';
import { WATER_BILLING } from '../../../../constants';
import CurrencyInput from '../../../Widgets/CurrencyInput';
import PhoneInput from '../../../Widgets/PhoneInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  children?: React.ReactNode;
};

function WaterBilling({ section, setSectionValues, children }: Props) {
  const radioGroupName = useId();
  const averageComparative = 'averageComparative';
  const averageFamily = 'averageFamily';

  function onEstimatedMonthlyChargeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.ESTIMATED_MONTHLY_CHARGE]: value,
    });
  }

  function onEstimatedMonthlyChargeSelection(event: React.ChangeEvent<HTMLInputElement>): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.AVERAGE_COMPARATIVE]: id === averageComparative,
      [WATER_BILLING.FIELD_NAME.AVERAGE_FAMILY]: id === averageFamily,
    });
  }

  function onPhoneNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.PHONE_NUMBER]: value,
    });
  }

  function onMailingAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.MAILING_ADDRESS]: value,
    });
  }

  function onEmailAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.EMAIL_ADDRESS]: value,
    });
  }

  function onTimeAndDatesChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.TIME_AND_DATES]: value,
    });
  }

  function onWaterDueDateChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.WATER_DUE_DATE]: value,
    });
  }

  function onWaterPaymentProcedureChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_BILLING.FIELD_NAME.WATER_PAYMENT_PROCEDURES]: value,
    });
  }

  return (
    <>
      <Paragraph textStyle="body-lg-bold">Water/sewer</Paragraph>
      <FormField
        required
        label={<Label>Estimated water bill</Label>}
        control={
          <CurrencyInput
            aria-labelledby="estimated-water-bill"
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.ESTIMATED_MONTHLY_CHARGE)}
            onChange={onEstimatedMonthlyChargeChange}
            onBlur={onEstimatedMonthlyChargeChange}
            endAdornment="per month"
          />
        }
      />
      <Box css={{ marginTop: 'layout.tight' }} />
      <FieldSet required legend={<Legend>How did you estimate the water bill?</Legend>}>
        <LabeledControl
          controlId={averageComparative}
          label={
            <Label>
              The average or median bill for water service for comparative dwelling units at the property over any three
              of the past six months
            </Label>
          }
          control={
            <Radio
              name={radioGroupName}
              checked={section.getFieldValue(WATER_BILLING.FIELD_NAME.AVERAGE_COMPARATIVE)}
              onChange={onEstimatedMonthlyChargeSelection}
            />
          }
        />
        <LabeledControl
          controlId={averageFamily}
          label={
            <Label>
              The average indoor water use of a family of four of approximately 200 gallons per day, and including all
              other monthly charges that will be assessed
            </Label>
          }
          control={
            <Radio
              name={radioGroupName}
              checked={section.getFieldValue(WATER_BILLING.FIELD_NAME.AVERAGE_FAMILY)}
              onChange={onEstimatedMonthlyChargeSelection}
            />
          }
        />
      </FieldSet>
      <FormField
        required
        css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}
        label={<Label>When is the water bill due date?</Label>}
        control={
          <Input
            aria-labelledby="water-due-date"
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.WATER_DUE_DATE)}
            onChange={onWaterDueDateChange}
          />
        }
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>What are the payment procedures for the water bill?</Label>}
        control={
          <Textarea
            aria-labelledby="water-payment-procedures"
            css={{ resize: 'vertical' }}
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.WATER_PAYMENT_PROCEDURES)}
            onChange={onWaterPaymentProcedureChange}
          />
        }
      />
      {children}
      <InlineFeedback>
        The tenant may contact you with questions about water billing with the information provided below
      </InlineFeedback>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Mailing address</Label>}
        control={
          <Textarea
            aria-labelledby="water-billing-mailing-address"
            css={{ resize: 'vertical' }}
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.MAILING_ADDRESS)}
            onChange={onMailingAddressChange}
          />
        }
        description={<FormHelp>Street, city, state, ZIP code</FormHelp>}
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Email address</Label>}
        control={
          <Input
            aria-labelledby="water-billing-email-address"
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.EMAIL_ADDRESS)}
            onChange={onEmailAddressChange}
            type="email"
          />
        }
        description={<FormHelp>This can either be the utility’s email or a personal email</FormHelp>}
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Phone number</Label>}
        control={
          <PhoneInput
            aria-labelledby="water-billing-phone-number"
            data-field-name={section.getField(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)?.fieldName}
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)}
            error={section.getField(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)?.hasError}
            onChange={onPhoneNumberChange}
          />
        }
        description={
          section.getField(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)?.hasError ? (
            <FormHelp>{section.getField(WATER_BILLING.FIELD_NAME.PHONE_NUMBER)?.getErrorMessage()}</FormHelp>
          ) : (
            <FormHelp>Local or toll-free numbers only</FormHelp>
          )
        }
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>During what times and days can the tenant call you?</Label>}
        control={
          <Textarea
            aria-labelledby="water-billing-schedule"
            css={{ resize: 'vertical' }}
            value={section.getFieldValue(WATER_BILLING.FIELD_NAME.TIME_AND_DATES)}
            onChange={onTimeAndDatesChange}
          />
        }
        description={
          <FormHelp>Include timezones if you live in a different timezone than where the property is located</FormHelp>
        }
      />
    </>
  );
}

WaterBilling.description = 'Asks if the unit has a water submeter';

export default WaterBilling;
